/**
 * @module core/search/vector-stores
 *
 * Powers search, embeddings, and semantic retrieval behavior for Vector Stores.
 */
import type { PmSettings } from "../../types/index.js";
import { mkdir, readFile, stat, unlink } from "node:fs/promises";
import { basename, dirname, isAbsolute, join, resolve } from "node:path";
import {
  executeSearchJsonRequest,
  normalizeSearchHttpTimeoutMs,
  resolveSearchHttpFetcher,
} from "./http-client.js";
import type { SearchHttpFetcher, SearchHttpResponse } from "./http-client.js";
import {
  isFiniteNumberArray,
  toErrorMessage,
  toNonEmptyString,
  trimTrailingSlashes,
} from "../shared/primitives.js";
import { writeFileAtomic } from "../fs/fs-utils.js";

/**
 * Restricts vector store name values accepted by command, SDK, and storage contracts.
 */
export type VectorStoreName = "qdrant" | "lancedb";

/**
 * Documents the qdrant vector store config payload exchanged by command, SDK, and package integrations.
 */
export interface QdrantVectorStoreConfig {
  name: "qdrant";
  url: string;
  collection_name?: string;
  api_key?: string;
}

/**
 * Documents the lance db vector store config payload exchanged by command, SDK, and package integrations.
 */
export interface LanceDbVectorStoreConfig {
  name: "lancedb";
  path: string;
  collection_name?: string;
}

/**
 * Restricts vector store config values accepted by command, SDK, and storage contracts.
 */
export type VectorStoreConfig = QdrantVectorStoreConfig | LanceDbVectorStoreConfig;

/**
 * Documents the vector store resolution payload exchanged by command, SDK, and package integrations.
 */
export interface VectorStoreResolution {
  active: VectorStoreConfig | null;
  available: VectorStoreConfig[];
}

/**
 * Documents the vector store request target payload exchanged by command, SDK, and package integrations.
 */
export interface VectorStoreRequestTarget {
  store: VectorStoreName;
  query_target: string;
  upsert_target: string;
}

/**
 * Documents the vector query plan payload exchanged by command, SDK, and package integrations.
 */
export interface VectorQueryPlan {
  target: VectorStoreRequestTarget;
  method: "POST" | "LOCAL";
  headers: Record<string, string>;
  body: Record<string, unknown>;
}

/**
 * Documents the vector record payload exchanged by command, SDK, and package integrations.
 */
export interface VectorRecord {
  id: string;
  vector: number[];
  payload?: Record<string, unknown>;
}

/**
 * Documents the vector upsert plan payload exchanged by command, SDK, and package integrations.
 */
export interface VectorUpsertPlan {
  target: VectorStoreRequestTarget;
  method: "POST" | "LOCAL";
  headers: Record<string, string>;
  body: Record<string, unknown>;
}

/**
 * Documents the vector delete plan payload exchanged by command, SDK, and package integrations.
 */
export interface VectorDeletePlan {
  target: VectorStoreRequestTarget;
  method: "POST" | "LOCAL";
  headers: Record<string, string>;
  body: Record<string, unknown>;
}

/**
 * Documents the vector query hit payload exchanged by command, SDK, and package integrations.
 */
export interface VectorQueryHit {
  id: string;
  score: number;
  payload?: Record<string, unknown>;
}

/**
 * Documents the vector upsert result payload exchanged by command, SDK, and package integrations.
 */
export interface VectorUpsertResult {
  status: string;
}

/**
 * Restricts vector http response values accepted by command, SDK, and storage contracts.
 */
export type VectorHttpResponse = SearchHttpResponse;

/**
 * Restricts vector request fetcher values accepted by command, SDK, and storage contracts.
 */
export type VectorRequestFetcher = SearchHttpFetcher<VectorHttpResponse>;

/**
 * Documents the execute vector request options payload exchanged by command, SDK, and package integrations.
 */
export interface ExecuteVectorRequestOptions {
  timeout_ms?: number;
  fetcher?: VectorRequestFetcher;
  warnings?: string[];
}

type VectorSettingsInput = {
  vector_store?: {
    collection_name?: string;
    qdrant?: {
      url?: string;
      api_key?: string;
    };
    lancedb?: {
      path?: string;
    };
  };
};

type RemoteVectorMethod = "DELETE" | "POST" | "PUT";

const DEFAULT_COLLECTION = "pm_items";
const COLLECTION_SANITIZE_PATTERN = /[^a-zA-Z0-9_-]/g;
const MAX_COLLECTION_NAME_LENGTH = 128;
const LANCE_DB_LOCAL_SNAPSHOT_DIR = ".pm-cli-local-vectors";
const LANCE_DB_LOCAL_SNAPSHOT_VERSION = 1;
interface LanceDbLocalTableCacheEntry {
  records: Map<string, VectorRecord>;
  mtimeMs: number | null;
  size: number | null;
}

const lanceDbLocalTables = new Map<string, LanceDbLocalTableCacheEntry>();

function resolveStoreCollectionName(store: VectorStoreConfig): string {
  const candidate = toNonEmptyString(store.collection_name) ?? DEFAULT_COLLECTION;
  return candidate.replaceAll(COLLECTION_SANITIZE_PATTERN, "_").slice(0, MAX_COLLECTION_NAME_LENGTH);
}

function normalizeVector(value: unknown): number[] {
  if (!isFiniteNumberArray(value) || value.length === 0) {
    throw new Error("Vector values must be a non-empty numeric array");
  }
  return [...value];
}

function normalizeLimit(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error("Vector query limit must be a positive number");
  }
  return Math.floor(value);
}

function normalizeQdrantQueryResponse(payload: unknown): VectorQueryHit[] {
  const result = (payload as { result?: unknown }).result;
  if (!Array.isArray(result)) {
    throw new TypeError("Qdrant query response must include a result array");
  }
  const hits = result.map((entry, index) => {
    const idValue = (entry as { id?: unknown }).id;
    const idCandidate = typeof idValue === "number" ? String(idValue) : idValue;
    const id = toNonEmptyString(idCandidate);
    if (!id) {
      throw new Error(`Qdrant query response entry at index ${index} is missing a non-empty id`);
    }

    const score = (entry as { score?: unknown }).score;
    if (typeof score !== "number" || !Number.isFinite(score)) {
      throw new TypeError(`Qdrant query response entry at index ${index} is missing a finite numeric score`);
    }

    const payloadValue = (entry as { payload?: unknown }).payload;
    if (
      payloadValue !== undefined &&
      (typeof payloadValue !== "object" || payloadValue === null || Array.isArray(payloadValue))
    ) {
      throw new Error(`Qdrant query response entry at index ${index} must provide payload as an object when set`);
    }

    return {
      id,
      score,
      ...(payloadValue ? { payload: payloadValue as Record<string, unknown> } : {}),
    };
  });
  hits.sort((left, right) => {
    if (left.score !== right.score) {
      return right.score - left.score;
    }
    return left.id.localeCompare(right.id);
  });
  return hits;
}

function normalizeQdrantUpsertResponse(payload: unknown): VectorUpsertResult {
  const nestedStatus = toNonEmptyString((payload as { result?: { status?: unknown } }).result?.status);
  if (nestedStatus) {
    return { status: nestedStatus };
  }
  const topLevelStatus = toNonEmptyString((payload as { status?: unknown }).status);
  if (topLevelStatus) {
    return { status: topLevelStatus };
  }
  throw new Error("Qdrant upsert response must include status metadata");
}

async function executeRemoteVectorPlan(
  endpoint: string,
  plan: {
    method: RemoteVectorMethod;
    headers: Record<string, string>;
    body: Record<string, unknown>;
  },
  timeoutMs: number,
  fetcher: VectorRequestFetcher,
  requestKind: "query" | "upsert" | "delete",
): Promise<unknown> {
  return await executeSearchJsonRequest({
    endpoint,
    method: plan.method,
    headers: plan.headers,
    body: plan.body,
    timeoutMs,
    fetcher,
    requestLabel: `Vector ${requestKind} request`,
    responseLabel: `Vector ${requestKind} response`,
  });
}

function resolveQdrantStore(settings: VectorSettingsInput): QdrantVectorStoreConfig | null {
  const url = toNonEmptyString(settings.vector_store?.qdrant?.url);
  if (!url) {
    return null;
  }
  const collectionName = toNonEmptyString(settings.vector_store?.collection_name);
  const apiKey = toNonEmptyString(settings.vector_store?.qdrant?.api_key);
  return {
    name: "qdrant",
    url,
    ...(collectionName && collectionName !== DEFAULT_COLLECTION ? { collection_name: collectionName } : {}),
    ...(apiKey ? { api_key: apiKey } : {}),
  };
}

/**
 * Derive the workspace root a workspace-relative store path is anchored to.
 * A conventional pm root (`<workspace>/.agents/pm`) maps to `<workspace>`;
 * an explicit bare root (`--path <dir>`) anchors to the root itself.
 */
function resolveWorkspaceRootFromPmRoot(pmRoot: string): string {
  const resolved = resolve(pmRoot);
  const parent = dirname(resolved);
  if (basename(resolved) === "pm" && basename(parent) === ".agents") {
    return dirname(parent);
  }
  return resolved;
}

/**
 * Anchor a relative lancedb path (settings store workspace-relative paths such
 * as the `.agents/pm/search/lancedb/` default) to the pm workspace root instead
 * of the process cwd. Without this, any pm invocation from a workspace
 * subdirectory (including detached background refresh children inheriting the
 * parent cwd) silently creates a second vector store under that subdirectory.
 */
function anchorLanceDbStorePath(storePath: string, pmRoot: string | undefined): string {
  if (!pmRoot || isAbsolute(storePath)) {
    return storePath;
  }
  return resolve(resolveWorkspaceRootFromPmRoot(pmRoot), storePath);
}

function resolveLanceDbStore(settings: VectorSettingsInput, pmRoot?: string): LanceDbVectorStoreConfig | null {
  const lancedbPath = toNonEmptyString(settings.vector_store?.lancedb?.path);
  if (!lancedbPath) {
    return null;
  }
  const collectionName = toNonEmptyString(settings.vector_store?.collection_name);
  return {
    name: "lancedb",
    path: anchorLanceDbStorePath(lancedbPath, pmRoot),
    ...(collectionName && collectionName !== DEFAULT_COLLECTION ? { collection_name: collectionName } : {}),
  };
}

function normalizeVectorRecords(records: VectorRecord[]): VectorRecord[] {
  if (!Array.isArray(records) || records.length === 0) {
    throw new Error("Vector upsert records must include at least one entry");
  }
  return records.map((record, index) => {
    const id = toNonEmptyString(record.id);
    if (!id) {
      throw new Error(`Vector upsert record at index ${index} is missing a non-empty id`);
    }
    const vector = normalizeVector(record.vector);
    const payload = record.payload;
    if (payload !== undefined && (typeof payload !== "object" || payload === null || Array.isArray(payload))) {
      throw new Error(`Vector upsert record at index ${index} must provide payload as an object when set`);
    }
    return {
      id,
      vector,
      ...(payload ? { payload } : {}),
    };
  });
}

function normalizeVectorDeleteIds(ids: string[]): string[] {
  if (!Array.isArray(ids) || ids.length === 0) {
    throw new Error("Vector delete ids must include at least one entry");
  }
  const normalizedIds = ids
    .map((id, index) => {
      const normalized = toNonEmptyString(id);
      if (!normalized) {
        throw new Error(`Vector delete id at index ${index} is missing a non-empty value`);
      }
      return normalized;
    })
    .sort((left, right) => left.localeCompare(right));
  const uniqueIds: string[] = [];
  for (const id of normalizedIds) {
    if (uniqueIds.at(-1) !== id) {
      uniqueIds.push(id);
    }
  }
  return uniqueIds;
}

function resolveQdrantDeleteTarget(upsertTarget: string): string {
  return upsertTarget.replace(/\/points\?wait=true$/, "/points/delete?wait=true");
}

function resolveQdrantCollectionTarget(upsertTarget: string): string {
  return upsertTarget.replace(/\/points\?wait=true$/, "");
}

function buildQdrantJsonHeaders(store: QdrantVectorStoreConfig): Record<string, string> {
  return {
    "content-type": "application/json",
    ...(store.api_key ? { "api-key": store.api_key } : {}),
  };
}

function getLanceDbLocalTableKey(storePath: string, table: string): string {
  return getLanceDbSnapshotPath(storePath, table);
}

function getLanceDbSnapshotPath(storePath: string, table: string): string {
  return join(resolve(storePath), LANCE_DB_LOCAL_SNAPSHOT_DIR, `${table}.json`);
}

function isNodeErrorWithCode(error: unknown, code: string): boolean {
  return typeof error === "object" && error !== null && (error as { code?: unknown }).code === code;
}

function normalizeSnapshotRecord(entry: unknown, index: number, snapshotPath: string): VectorRecord {
  const id = toNonEmptyString((entry as { id?: unknown }).id);
  if (!id) {
    throw new Error(`LanceDB local snapshot '${snapshotPath}' record at index ${index} is missing a non-empty id`);
  }
  const vector = normalizeVector((entry as { vector?: unknown }).vector);
  const payload = (entry as { payload?: unknown }).payload;
  if (payload !== undefined && (typeof payload !== "object" || payload === null || Array.isArray(payload))) {
    throw new Error(
      `LanceDB local snapshot '${snapshotPath}' record '${id}' must provide payload as an object when set`,
    );
  }
  return {
    id,
    vector,
    ...(payload ? { payload: payload as Record<string, unknown> } : {}),
  };
}

function parseLanceDbSnapshot(snapshotPath: string, expectedTable: string, raw: string): Map<string, VectorRecord> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch (error) {
    throw new Error(`LanceDB local snapshot at '${snapshotPath}' is not valid JSON: ${toErrorMessage(error)}`);
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error(`LanceDB local snapshot at '${snapshotPath}' must be a JSON object`);
  }

  const version = (parsed as { version?: unknown }).version;
  if (version !== LANCE_DB_LOCAL_SNAPSHOT_VERSION) {
    throw new Error(
      `LanceDB local snapshot at '${snapshotPath}' must include version=${LANCE_DB_LOCAL_SNAPSHOT_VERSION}`,
    );
  }

  const table = toNonEmptyString((parsed as { table?: unknown }).table);
  if (!table) {
    throw new Error(`LanceDB local snapshot at '${snapshotPath}' must include a non-empty table value`);
  }
  if (table !== expectedTable) {
    throw new Error(
      `LanceDB local snapshot at '${snapshotPath}' table mismatch: expected '${expectedTable}', received '${table}'`,
    );
  }

  const recordsValue = (parsed as { records?: unknown }).records;
  if (!Array.isArray(recordsValue)) {
    throw new TypeError(`LanceDB local snapshot at '${snapshotPath}' must include a records array`);
  }

  const tableRecords = new Map<string, VectorRecord>();
  for (let index = 0; index < recordsValue.length; index += 1) {
    const record = normalizeSnapshotRecord(recordsValue[index], index, snapshotPath);
    tableRecords.set(record.id, record);
  }
  return tableRecords;
}

async function readLanceDbSnapshotStats(snapshotPath: string): Promise<{ mtimeMs: number; size: number } | null> {
  try {
    const stats = await stat(snapshotPath);
    return { mtimeMs: stats.mtimeMs, size: stats.size };
  } catch (error) {
    if (!isNodeErrorWithCode(error, "ENOENT")) {
      throw error;
    }
    return null;
  }
}

function resolveMissingLanceDbSnapshotCache(
  key: string,
  cached: LanceDbLocalTableCacheEntry | undefined,
): Map<string, VectorRecord> | null {
  if (!cached) {
    return null;
  }
  if (cached.mtimeMs === null && cached.size === null && cached.records.size === 0) {
    return cached.records;
  }
  const loaded = new Map<string, VectorRecord>();
  lanceDbLocalTables.set(key, { records: loaded, mtimeMs: null, size: null });
  return loaded;
}

function cachedLanceDbSnapshotMatches(
  cached: LanceDbLocalTableCacheEntry | undefined,
  snapshotStats: { mtimeMs: number; size: number } | null,
): cached is LanceDbLocalTableCacheEntry {
  return (
    cached !== undefined &&
      snapshotStats !== null &&
      cached.mtimeMs === snapshotStats.mtimeMs &&
      cached.size === snapshotStats.size
  );
}

async function loadLanceDbLocalTable(storePath: string, table: string): Promise<Map<string, VectorRecord>> {
  const key = getLanceDbLocalTableKey(storePath, table);
  const snapshotPath = getLanceDbSnapshotPath(storePath, table);
  const snapshotStats = await readLanceDbSnapshotStats(snapshotPath);

  const cached = lanceDbLocalTables.get(key);
  if (!snapshotStats) {
    const resolved = resolveMissingLanceDbSnapshotCache(key, cached);
    if (resolved) {
      return resolved;
    }
  }
  if (cachedLanceDbSnapshotMatches(cached, snapshotStats)) {
    return cached.records;
  }

  let loaded = new Map<string, VectorRecord>();
  try {
    const raw = await readFile(snapshotPath, "utf8");
    loaded = parseLanceDbSnapshot(snapshotPath, table, raw);
  } catch (error) {
    if (!isNodeErrorWithCode(error, "ENOENT")) {
      throw error;
    }
  }
  lanceDbLocalTables.set(key, {
    records: loaded,
    mtimeMs: snapshotStats?.mtimeMs ?? null,
    size: snapshotStats?.size ?? null,
  });
  return loaded;
}

function buildSnapshotRecords(table: Map<string, VectorRecord>): VectorRecord[] {
  const records = [...table.values()];
  records.sort((left, right) => left.id.localeCompare(right.id));
  return records.map((record) => ({
    id: record.id,
    vector: [...record.vector],
    ...(record.payload ? { payload: record.payload } : {}),
  }));
}

async function removeSnapshotFile(snapshotPath: string): Promise<void> {
  try {
    await unlink(snapshotPath);
  } catch (error) {
    if (!isNodeErrorWithCode(error, "ENOENT")) {
      throw new Error(`LanceDB local snapshot delete failed at '${snapshotPath}': ${toErrorMessage(error)}`);
    }
  }
}

async function persistLanceDbLocalTable(storePath: string, tableName: string, table: Map<string, VectorRecord>): Promise<void> {
  const snapshotPath = getLanceDbSnapshotPath(storePath, tableName);
  if (table.size === 0) {
    await removeSnapshotFile(snapshotPath);
    return;
  }

  const snapshotDir = dirname(snapshotPath);
  try {
    await mkdir(snapshotDir, { recursive: true });
  } catch (error) {
    throw new Error(
      `LanceDB local snapshot directory create failed at '${snapshotDir}': ${toErrorMessage(error)}`,
    );
  }

  // Serialize compactly: the snapshot is an internal, gitignored cache re-read via
  // JSON.parse (whitespace-agnostic). Pretty-printing put every embedding float on its
  // own indented line, roughly doubling file size and parse/serialize cost — a real
  // per-mutation latency tax on large indexes. Compact JSON keeps it instant.
  const serialized = `${JSON.stringify({
    version: LANCE_DB_LOCAL_SNAPSHOT_VERSION,
    table: tableName,
    records: buildSnapshotRecords(table),
  })}\n`;
  try {
    await writeFileAtomic(snapshotPath, serialized);
  } catch (error) {
    throw new Error(`LanceDB local snapshot write failed at '${snapshotPath}': ${toErrorMessage(error)}`);
  }
}

async function resetLanceDbLocalTable(storePath: string, tableName: string): Promise<void> {
  const key = getLanceDbLocalTableKey(storePath, tableName);
  const snapshotPath = getLanceDbSnapshotPath(storePath, tableName);
  await removeSnapshotFile(snapshotPath);
  lanceDbLocalTables.delete(key);
}

function l2Norm(vector: number[]): number {
  let sumSq = 0;
  for (let index = 0; index < vector.length; index += 1) {
    sumSq += vector[index] * vector[index];
  }
  return Math.sqrt(sumSq);
}

function cosineSimilarity(left: number[], right: number[]): number {
  let dotProd = 0;
  for (let index = 0; index < left.length; index += 1) {
    dotProd += left[index] * right[index];
  }
  const leftNorm = l2Norm(left);
  const rightNorm = l2Norm(right);
  if (leftNorm === 0 || rightNorm === 0) {
    return 0;
  }
  return dotProd / (leftNorm * rightNorm);
}

/**
 * Implements resolve vector stores for the public runtime surface of this module.
 */
export function resolveVectorStores(settings: PmSettings | VectorSettingsInput, pmRoot?: string): VectorStoreResolution {
  const qdrant = resolveQdrantStore(settings);
  const lancedb = resolveLanceDbStore(settings, pmRoot);
  const available = [qdrant, lancedb].filter((entry): entry is VectorStoreConfig => entry !== null);
  // Honor `settings.vector_store.adapter` when set: if both built-in stores
  // are configured, the preferred adapter wins; otherwise fall back to the
  // first available entry (preserves the previous tie-break: qdrant > lancedb).
  // Match case-insensitively so "Qdrant" / "LanceDB" / "lancedb" all work.
  const preferredName = toNonEmptyString(
    (settings as { vector_store?: { adapter?: unknown } }).vector_store?.adapter,
  );
  const preferredKey = preferredName ? preferredName.toLowerCase() : null;
  const preferred = preferredKey
    ? available.find((entry) => entry.name === preferredKey)
    : undefined;
  return {
    active: preferred ?? available[0] ?? null,
    available,
  };
}

/**
 * Implements resolve vector store request target for the public runtime surface of this module.
 */
export function resolveVectorStoreRequestTarget(store: VectorStoreConfig): VectorStoreRequestTarget {
  const collectionName = resolveStoreCollectionName(store);
  if (store.name === "qdrant") {
    const baseUrl = trimTrailingSlashes(store.url);
    const encodedCollection = encodeURIComponent(collectionName);
    return {
      store: "qdrant",
      query_target: `${baseUrl}/collections/${encodedCollection}/points/search`,
      upsert_target: `${baseUrl}/collections/${encodedCollection}/points?wait=true`,
    };
  }
  const encodedPath = encodeURIComponent(store.path);
  const encodedCollection = encodeURIComponent(collectionName);
  return {
    store: "lancedb",
    query_target: `lancedb://${encodedPath}#${encodedCollection}`,
    upsert_target: `lancedb://${encodedPath}#${encodedCollection}`,
  };
}

/**
 * Implements build vector query plan for the public runtime surface of this module.
 */
export function buildVectorQueryPlan(store: VectorStoreConfig, vector: number[], limit: number): VectorQueryPlan {
  const target = resolveVectorStoreRequestTarget(store);
  const normalizedVector = normalizeVector(vector);
  const normalizedLimit = normalizeLimit(limit);
  if (store.name === "qdrant") {
    return {
      target,
      method: "POST",
      headers: buildQdrantJsonHeaders(store),
      body: {
        vector: normalizedVector,
        limit: normalizedLimit,
        with_payload: true,
      },
    };
  }
  return {
    target,
    method: "LOCAL",
    headers: {},
    body: {
      table: resolveStoreCollectionName(store),
      vector: normalizedVector,
      limit: normalizedLimit,
    },
  };
}

/**
 * Implements build vector upsert plan for the public runtime surface of this module.
 */
export function buildVectorUpsertPlan(store: VectorStoreConfig, records: VectorRecord[]): VectorUpsertPlan {
  const target = resolveVectorStoreRequestTarget(store);
  const normalizedRecords = normalizeVectorRecords(records);
  if (store.name === "qdrant") {
    return {
      target,
      method: "POST",
      headers: buildQdrantJsonHeaders(store),
      body: {
        points: normalizedRecords,
      },
    };
  }
  return {
    target,
    method: "LOCAL",
    headers: {},
    body: {
      table: resolveStoreCollectionName(store),
      records: normalizedRecords,
    },
  };
}

/**
 * Implements build vector delete plan for the public runtime surface of this module.
 */
export function buildVectorDeletePlan(store: VectorStoreConfig, ids: string[]): VectorDeletePlan {
  const target = resolveVectorStoreRequestTarget(store);
  const normalizedIds = normalizeVectorDeleteIds(ids);
  if (store.name === "qdrant") {
    return {
      target,
      method: "POST",
      headers: buildQdrantJsonHeaders(store),
      body: {
        points: normalizedIds,
      },
    };
  }
  return {
    target,
    method: "LOCAL",
    headers: {},
    body: {
      table: resolveStoreCollectionName(store),
      ids: normalizedIds,
    },
  };
}

/**
 * Implements execute vector query for the public runtime surface of this module.
 */
export async function executeVectorQuery(
  store: VectorStoreConfig,
  vector: number[],
  limit: number,
  options: ExecuteVectorRequestOptions = {},
): Promise<VectorQueryHit[]> {
  const plan = buildVectorQueryPlan(store, vector, limit);
  if (plan.method === "LOCAL") {
    const lanceDbStore = store as LanceDbVectorStoreConfig;
    const queryBody = plan.body as {
      table: string;
      vector: number[];
      limit: number;
    };
    const table = await loadLanceDbLocalTable(lanceDbStore.path, queryBody.table);
    if (table.size === 0) {
      return [];
    }
    const queryVector = normalizeVector(queryBody.vector);
    const queryLimit = normalizeLimit(queryBody.limit);
    const hits: VectorQueryHit[] = [];
    let dimensionMismatchCount = 0;
    for (const record of table.values()) {
      if (record.vector.length !== queryVector.length) {
        dimensionMismatchCount++;
        continue;
      }
      hits.push({
        id: record.id,
        score: cosineSimilarity(queryVector, record.vector),
        ...(record.payload ? { payload: record.payload } : {}),
      });
    }
    if (dimensionMismatchCount > 0 && options.warnings) {
      options.warnings.push(
        `vector_dimension_mismatch:${dimensionMismatchCount} records skipped (expected ${queryVector.length} dimensions)`,
      );
    }
    hits.sort((left, right) => {
      if (left.score !== right.score) {
        return right.score - left.score;
      }
      return left.id.localeCompare(right.id);
    });
    return hits.slice(0, queryLimit);
  }
  const timeoutMs = normalizeSearchHttpTimeoutMs(options.timeout_ms, "Vector request");
  const fetcher = resolveSearchHttpFetcher(options.fetcher, "Vector request");
  const payload = await executeRemoteVectorPlan(
    plan.target.query_target,
    {
      method: "POST",
      headers: plan.headers,
      body: plan.body,
    },
    timeoutMs,
    fetcher,
    "query",
  );
  return normalizeQdrantQueryResponse(payload);
}

/**
 * Implements execute vector upsert for the public runtime surface of this module.
 */
export async function executeVectorUpsert(
  store: VectorStoreConfig,
  records: VectorRecord[],
  options: ExecuteVectorRequestOptions = {},
): Promise<VectorUpsertResult> {
  const plan = buildVectorUpsertPlan(store, records);
  if (plan.method === "LOCAL") {
    const lanceDbStore = store as LanceDbVectorStoreConfig;
    const upsertBody = plan.body as {
      table: string;
      records: VectorRecord[];
    };
    const key = getLanceDbLocalTableKey(lanceDbStore.path, upsertBody.table);
    const table = await loadLanceDbLocalTable(lanceDbStore.path, upsertBody.table);
    for (const record of upsertBody.records) {
      table.set(record.id, record);
    }
    await persistLanceDbLocalTable(lanceDbStore.path, upsertBody.table, table);
    const snapshotPath = getLanceDbSnapshotPath(lanceDbStore.path, upsertBody.table);
    const snapshotStats = await stat(snapshotPath);
    lanceDbLocalTables.set(key, {
      records: table,
      mtimeMs: snapshotStats.mtimeMs,
      size: snapshotStats.size,
    });
    return { status: "ok" };
  }
  const timeoutMs = normalizeSearchHttpTimeoutMs(options.timeout_ms, "Vector request");
  const fetcher = resolveSearchHttpFetcher(options.fetcher, "Vector request");
  const payload = await executeRemoteVectorPlan(
    plan.target.upsert_target,
    {
      method: "POST",
      headers: plan.headers,
      body: plan.body,
    },
    timeoutMs,
    fetcher,
    "upsert",
  );
  return normalizeQdrantUpsertResponse(payload);
}

/**
 * Implements execute vector delete for the public runtime surface of this module.
 */
export async function executeVectorDelete(
  store: VectorStoreConfig,
  ids: string[],
  options: ExecuteVectorRequestOptions = {},
): Promise<VectorUpsertResult> {
  const plan = buildVectorDeletePlan(store, ids);
  if (plan.method === "LOCAL") {
    const lanceDbStore = store as LanceDbVectorStoreConfig;
    const deleteBody = plan.body as {
      table: string;
      ids: string[];
    };
    const key = getLanceDbLocalTableKey(lanceDbStore.path, deleteBody.table);
    const table = await loadLanceDbLocalTable(lanceDbStore.path, deleteBody.table);
    if (table.size === 0) {
      return { status: "ok" };
    }
    for (const id of deleteBody.ids) {
      table.delete(id);
    }
    await persistLanceDbLocalTable(lanceDbStore.path, deleteBody.table, table);
    if (table.size === 0) {
      lanceDbLocalTables.delete(key);
    } else {
      const snapshotPath = getLanceDbSnapshotPath(lanceDbStore.path, deleteBody.table);
      const snapshotStats = await stat(snapshotPath);
      lanceDbLocalTables.set(key, {
        records: table,
        mtimeMs: snapshotStats.mtimeMs,
        size: snapshotStats.size,
      });
    }
    return { status: "ok" };
  }
  const timeoutMs = normalizeSearchHttpTimeoutMs(options.timeout_ms, "Vector request");
  const fetcher = resolveSearchHttpFetcher(options.fetcher, "Vector request");
  const payload = await executeRemoteVectorPlan(
    resolveQdrantDeleteTarget(plan.target.upsert_target),
    {
      method: "POST",
      headers: plan.headers,
      body: plan.body,
    },
    timeoutMs,
    fetcher,
    "delete",
  );
  return normalizeQdrantUpsertResponse(payload);
}

/**
 * Implements execute vector reset for the public runtime surface of this module.
 */
export async function executeVectorReset(
  store: VectorStoreConfig,
  knownIds: string[] = [],
  options: ExecuteVectorRequestOptions = {},
  vectorDimension?: number,
): Promise<VectorUpsertResult> {
  if (store.name === "lancedb") {
    await resetLanceDbLocalTable(store.path, resolveStoreCollectionName(store));
    return { status: "ok" };
  }
  if (vectorDimension !== undefined) {
    if (!Number.isInteger(vectorDimension) || vectorDimension <= 0) {
      throw new Error("Vector reset dimension must be a positive integer when provided");
    }
    const target = resolveVectorStoreRequestTarget(store);
    const collectionTarget = resolveQdrantCollectionTarget(target.upsert_target);
    const timeoutMs = normalizeSearchHttpTimeoutMs(options.timeout_ms, "Vector request");
    const fetcher = resolveSearchHttpFetcher(options.fetcher, "Vector request");
    const headers = buildQdrantJsonHeaders(store);
    try {
      await executeRemoteVectorPlan(
        collectionTarget,
        {
          method: "DELETE",
          headers,
          body: {},
        },
        timeoutMs,
        fetcher,
        "delete",
      );
    } catch (error: unknown) {
      if (!toErrorMessage(error).includes("failed with status 404")) {
        throw error;
      }
    }
    const payload = await executeRemoteVectorPlan(
      collectionTarget,
      {
        method: "PUT",
        headers,
        body: {
          vectors: {
            size: vectorDimension,
            distance: "Cosine",
          },
        },
      },
      timeoutMs,
      fetcher,
      "upsert",
    );
    return normalizeQdrantUpsertResponse(payload);
  }
  const idsToDelete = knownIds.map((id) => id.trim()).filter((id) => id.length > 0);
  if (idsToDelete.length === 0) {
    return { status: "ok" };
  }
  return await executeVectorDelete(store, idsToDelete, options);
}
